import { alpha, createTheme, type Theme } from '@mui/material/styles'
import type { ButtonProps } from '@mui/material/Button'
import {
  blackVariants,
  blueDeepVariants,
  blueTechVariants,
  limeGreenVariants,
  redVariants,
  violetVariants,
  yellowVariants
} from './colors.ts'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

// Augment the theme so it supports MUI-X Date Picker
import '@mui/x-date-pickers/themeAugmentation'

import { inputClasses } from '@mui/material'

const brandColors = {
  white: '#ffffff',
  blueTech: blueTechVariants['900'],
  blueDeep: blueDeepVariants['900'],
  violet: violetVariants['700'],
  greenLime: limeGreenVariants['400'],
  lightGrey: '#757575'
}

const spacing = 8

const baseTheme = createTheme({
  spacing,
  palette: {
    mode: 'light',
    grey: blackVariants,
    primary: {
      light: blueDeepVariants['50'],
      main: brandColors.blueDeep,
      dark: blueDeepVariants['900'],
      contrastText: brandColors.white
    },
    secondary: {
      light: blueTechVariants['50'],
      main: brandColors.blueTech,
      dark: blueTechVariants['900'],
      contrastText: brandColors.white
    },
    background: {
      default: '#fafafa',
      paper: '#fffffc'
    },
    text: {
      primary: brandColors.blueDeep,
      secondary: blackVariants['700']
    },
    success: {
      light: limeGreenVariants['100'],
      main: limeGreenVariants['600'],
      dark: limeGreenVariants['900']
    },
    info: {
      light: violetVariants['100'],
      main: blueTechVariants['600'],
      dark: violetVariants['900']
    },
    error: {
      light: redVariants['100'],
      main: redVariants['600'],
      dark: redVariants['900']
    },
    warning: {
      light: yellowVariants['100'],
      main: yellowVariants['600'],
      dark: yellowVariants['900']
    }
  }
})

const selectableItemOverrides = {
  '&:hover': {
    color: baseTheme.palette.primary.main,
    backgroundColor: `${violetVariants['100']} !important`
  },
  '&.Mui-selected': {
    backgroundColor: `${baseTheme.palette.background.default} !important`,
    color: `${baseTheme.palette.primary.main} !important`,
    '&:hover': {
      backgroundColor: `${violetVariants['100']} !important`
    }
  }
}

type ButtonColor = ButtonProps['color']

const buttonMainColor = (theme: Theme, color?: ButtonColor) => {
  if (color && color !== 'inherit') return theme.palette[color].main
  if (color === 'inherit') return 'inherit'
  return theme.palette.primary.main
}

const buttonDarkColor = (theme: Theme, color?: ButtonColor) =>
  color && color !== 'inherit'
    ? { color: theme.palette[color].dark, borderColor: theme.palette[color].dark }
    : {}

const lightBgStates = (theme: Theme, color?: ButtonColor) => {
  const light = color && color !== 'inherit' ? theme.palette[color].light : null
  return {
    '&:hover': {
      backgroundColor: light ?? alpha(theme.palette.text.primary, 0.04)
    },
    '&:active': {
      backgroundColor: light ?? alpha(theme.palette.text.primary, 0.12)
    }
  }
}

export const themeOptions = createTheme({
  palette: baseTheme.palette,
  spacing: spacing,
  shape: {
    borderRadius: 2
  },
  typography: {
    fontFamily: [
      'Britti Sans Variable',
      'Roboto',
      'Helvetica',
      'Arial',
      'sans-serif',
      'Geist Mono, monospaced'
    ].join(','),

    // Module title
    h1: {
      fontSize: baseTheme.typography.pxToRem(40),
      lineHeight: 1.3,
      fontWeight: 600
    },
    h3: {
      fontSize: baseTheme.typography.pxToRem(32),
      lineHeight: 1,
      fontWeight: 700,
      letterSpacing: '2%'
    },
    h4: {
      fontSize: baseTheme.typography.pxToRem(24),
      fontWeight: 400
    },
    h6: {
      fontWeight: 500
    },
    h7: {
      fontSize: baseTheme.typography.pxToRem(16),
      fontWeight: 700
    },
    // Big card header type
    hCard: {
      fontWeight: 700,
      fontSize: baseTheme.typography.pxToRem(24),
      lineHeight: 'normal'
    },
    hFigureCard: {
      fontFamily: ['Geist Mono, monospaced'].join(''),
      fontWeight: 400,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(18),
      lineHeight: baseTheme.typography.pxToRem(18)
    },
    // Header of a data table
    hDataTable: {
      fontSize: baseTheme.typography.pxToRem(14),
      fontStyle: 'normal',
      fontWeight: 700
    },
    body2: {
      fontWeight: 400,
      fontSize: baseTheme.typography.pxToRem(16),
      color: '#02173C'
    },
    body3: {
      fontSize: baseTheme.typography.pxToRem(14)
    },
    subtitle2: {
      fontFamily: ['Geist Mono, monospaced'].join(''),
      fontWeight: 500,
      backgroundColor: '#91e091ff',
      color: '#020f02ff'
    },
    subtitle3: {
      fontWeight: 400,
      fontSize: baseTheme.typography.pxToRem(16)
    },
    subtitle4: {
      fontWeight: 400,
      fontSize: '16px'
    },
    subtitle5: {
      fontWeight: 600,
      fontSize: '24px'
    },
    subtitle6: {
      fontWeight: 600,
      fontSize: '20px'
    },
    tableCellPrimary: {
      fontSize: baseTheme.typography.pxToRem(14),
      fontWeight: 400
    },
    listingItemPrimary: {
      fontSize: baseTheme.typography.pxToRem(14),
      fontWeight: 400
    },
    tableCellSecondary: {
      fontFamily: 'Geist Mono, monospaced',
      fontSize: baseTheme.typography.pxToRem(14),
      fontWeight: 400
    },
    caption: {
      userSelect: 'none',
      msUserSelect: 'none',
      cursor: 'default'
    },
    propertyValue: {
      fontFamily: 'Geist Mono, monospaced',
      fontSize: '1em',
      fontWeight: 300
    },
    propertyLabel: {
      fontSize: '0.8em',
      fontWeight: 400
    },
    buttonText: {
      fontSize: '16px',
      fontWeight: 500,
      fontStyle: 'Medium'
    },
    indicationNote: {
      fontSize: '11px',
      fontWeight: 400,
      fontStyle: 'Regular'
    },
    body4: {
      fontSize: '12px',
      fontWeight: 400,
      fontStyle: 'Regular'
    }
  },
  components: {
    MuiButtonBase: { defaultProps: { disableRipple: true } },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          padding: baseTheme.spacing(1, 2),
          fontWeight: 500,
          boxShadow: 'none',
          textTransform: 'none',
          lineHeight: 1.6,
          '&:focus-visible': { border: '1.5px dashed currentColor' }
        },
        contained: ({ theme, ownerState: { color } }) => {
          const main = buttonMainColor(theme, color)
          const isPrimary = !color || color === 'primary'
          return {
            fontSize: baseTheme.typography.pxToRem(16),
            '&:hover, &:active': {
              backgroundColor: isPrimary ? violetVariants['600'] : alpha(main, 0.85)
            }
          }
        },
        outlined: ({ theme, ownerState: { color } }) => ({
          ...buttonDarkColor(theme, color),
          ...lightBgStates(theme, color)
        }),
        text: ({ theme, ownerState: { color } }) => ({
          ...buttonDarkColor(theme, color),
          ...lightBgStates(theme, color)
        })
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const elevation = ownerState.elevation || 0

          if (elevation === 0) return {}

          // Scale blur and spread based on elevation
          const blur = 24 - elevation * 2 // e.g., 14px at elevation 1, 24px at elevation 6
          const spread = 3
          const opacity = 0.03 + elevation * 0.02 // e.g., 0.05 at elevation 1, 0.15 at elevation 6

          return {
            boxShadow: `0 0 ${blur}px ${spread}px rgba(33, 33, 33, ${opacity})`
          }
        }
      },
      variants: [
        {
          props: { variant: 'gradient' },
          style: {
            background: `radial-gradient(circle at 20% 110%, rgba(100, 120, 182, 0.1), rgba(100, 120, 182, 0.00) 35%), radial-gradient(circle at -20% 100%, rgba(0, 183, 181, 0.18), rgba(0, 183, 181, 0.0) 34%), ${baseTheme.palette.background.paper}`
          }
        },
        {
          props: { variant: 'glass' },
          style: {
            border: `1px solid ${alpha(baseTheme.palette.background.paper, 0.1)}`,
            background: `linear-gradient(196deg, ${alpha(baseTheme.palette.background.paper, 0.42)} 22%, ${alpha(baseTheme.palette.background.paper, 0.12)} 100%)`,
            backdropFilter: 'blur(24px)',
            backfaceVisibility: 'hidden'
          }
        }
      ]
    },
    MuiCard: {
      defaultProps: {
        raised: false
      },
      styleOverrides: {
        root: {
          padding: spacing * 3
        }
      }
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: {
        root: {
          cursor: 'pointer',
          fontWeight: 500,
          fontSize: baseTheme.typography.pxToRem(16),
          textDecoration: 'none',
          '&:hover': {
            color: brandColors.violet
          },
          '&.Mui-disabled': {
            pointerEvents: 'none',
            cursor: 'default',
            color: baseTheme.palette.text.disabled,
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 'inherit'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: `${spacing}px ${spacing * 2}px ${spacing}px ${spacing * 2}px`,

          textEdge: 'cap',
          fontFamily: 'Britti Sans Variable, Comic Sans',
          fontSize: baseTheme.typography.pxToRem(16),
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: baseTheme.typography.pxToRem(20),
          textTransform: 'none',
          color: 'grey.600',
          minHeight: 'inherit',

          '&.Mui-selected': {
            color: 'primary.main',
            fontWeight: 600
          }
        }
      }
    },
    MuiAutocomplete: {
      defaultProps: {
        slotProps: {
          paper: {
            elevation: 6
          }
        }
      }
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: baseTheme.palette.background.paper,
          // Style pour les variant outline
          '& .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: brandColors.lightGrey // Default border
          },
          '&:hover .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: brandColors.lightGrey // Hover state
          },
          [`&.${inputClasses.focused} MuiPickersOutlinedInput-notchedOutline`]: {
            borderColor: brandColors.blueDeep // Focused state
          }
        }
      }
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: ExpandMoreIcon
      }
    },
    MuiList: {
      styleOverrides: {
        root: {
          paddingTop: 0,
          paddingBottom: 0
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          ...selectableItemOverrides
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          ...selectableItemOverrides,
          color: blackVariants['700']
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: baseTheme.palette.background.paper,
          // Style pour les variant outline
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: brandColors.lightGrey // Default border
          },
          // '&:hover .MuiOutlinedInput-notchedOutline': {
          //   borderColor: brandColors.lightGrey // Hover state
          // },
          [`&.${inputClasses.focused} .MuiOutlinedInput-notchedOutline`]: {
            borderColor: brandColors.blueDeep // Focused state
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 2
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          padding: baseTheme.spacing(1),
          backgroundColor: blueDeepVariants['900'],
          color: '#fff',
          fontSize: baseTheme.typography.pxToRem(12),
          fontWeight: 400,
          lineHeight: 'normal'
        },
        arrow: {
          color: blueDeepVariants['900']
        }
      }
    }
  }
})
